import { useSnackbar } from "notistack";
import { useState, VFC, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { CountryPath } from "src/entities/CountryPath";
import { AuthMeResponse } from "src/entities/v3/AuthMeResponse";
import { PaymentQuotaResponse } from "src/entities/v3/PaymentQuotaResponse";
import { PublisherResponseV3 } from "src/entities/v3/PublisherResponse";
import { useFetch } from "src/hooks/useFetch";
import { checkSubscripted } from "src/utils/checkSubscripted";
import getBrowserLanguage, {
  AvailLanguage,
} from "src/utils/getBrowserLanguage";
import { getCredit } from "src/utils/getCredit";
import { setHtmlLang } from "src/utils/setHtmlLang";
import { useAuth } from "../hooks/useAuth";
import DashboardLayout from "./layouts/DashboardLayout";
import SignInLayout from "./layouts/SignInLayout";
import AdditionalServicePage from "./pages/AdditionalServicePage";
import AiChatPage from "./pages/AiChatPage";
import CreateArticlePage from "./pages/CreateArticlePage";
import CreditsmanagementPage from "./pages/CreditsManagementPage";
import DashboardPage from "./pages/DashboardPage";
import EditArticlePage from "./pages/EditArticlePage";
import EditPublisherInfoPage from "./pages/EditPublisherInfoPage";
import ReportPage from "./pages/ReportPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SelectGenerationMethodPage from "./pages/SelectGenerationMethodPage";
import SignInPage from "./pages/SignInPage";
import SignUpPage from "./pages/SignUpPage";

const Root: VFC = () => {
  const auth = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [path, setPath] = useState<string>(getPath());

  const url = decodeURIComponent(window.location.href);
  const urlParam = new URLSearchParams(url.split("?")[1]).get("country");
  const queryCountry = getCountry(urlParam);
  const [country, setCountry] = useState<CountryPath>(
    queryCountry === "" ? "IDN" : queryCountry
  );
  const [defaultCountry, setDefaultCountry] = useState<string | undefined>();

  const [lang, setLang] = useState<AvailLanguage>(() => {
    const initialLang = getBrowserLanguage();
    setHtmlLang(initialLang);
    return initialLang;
  });

  // Update HTML lang attribute and body class when language changes
  useEffect(() => {
    setHtmlLang(lang);
    // Also update body class for Tailwind font
    // Remove existing font classes first
    document.body.classList.remove("font-sans-ja", "font-sans-en");
    if (lang === "jp") {
      document.body.classList.add("font-sans-ja");
    } else {
      document.body.classList.add("font-sans-en");
    }
  }, [lang]);

  const {
    data: publisherResponse,
    isLoading: isPublisherLoading,
    refetch: refetchPublisher,
  } = useFetch<PublisherResponseV3>({
    url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/publishers`,
    skip: !auth.isAuth,
    onError: () => {
      enqueueSnackbar(
        "An unexpected error has occurred. Please sign in again.",
        {
          variant: "error",
        }
      );
      auth.apiError();
    },
    onSuccess: (data) => {
      setDefaultCountry(data.country);
      if (queryCountry) {
        return;
      }
      // JPN users default to IDN for country selection
      if (data.country === "JPN") {
        if (
          window.location.pathname === "/dashboard" ||
          window.location.pathname === "/settings"
        ) {
          setCountry("IDN");
        }
        // Redirect from billing page for JPN users
        if (window.location.pathname === "/credits_management") {
          window.location.href = "/dashboard";
        }
      } else if (
        data.country === "IDN" ||
        data.country === "SGP" ||
        data.country === "MYS" ||
        data.country === "PHL" ||
        data.country === "THA" ||
        data.country === "VNM" ||
        data.country === "AUS" ||
        data.country === "USA"
      ) {
        if (
          window.location.pathname === "/dashboard" ||
          window.location.pathname === "/settings"
        ) {
          setCountry(data.country as CountryPath);
        }
      }
    },
  });

  const { data: paymentQuotaResponse, refetch: refetchPaymentQuota } =
    useFetch<PaymentQuotaResponse>({
      url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/payment/quota`,
      skip: !auth.isAuth,
      onError: () => {
        enqueueSnackbar(
          "An unexpected error has occurred. Please sign in again.",
          {
            variant: "error",
          }
        );
        auth.apiError();
      },
    });

  const {
    data: authMeResponse,
    isLoading: isAuthMeLoading,
    refetch: refetchAuthMe,
  } = useFetch<AuthMeResponse>({
    url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/auth/me`,
    skip: !auth.isAuth,
    onError: () => {
      enqueueSnackbar(
        "An unexpected error has occurred. Please sign in again.",
        {
          variant: "error",
        }
      );
      auth.apiError();
    },
  });

  // Combined refetch function for both publisher and auth/me data
  const refetchAll = async (): Promise<PublisherResponseV3> => {
    const [publisherData] = await Promise.all([
      refetchPublisher(),
      refetchPaymentQuota(),
      refetchAuthMe(),
    ]);
    return publisherData;
  };

  // Get font class based on language
  const getFontClass = () => {
    if (lang === "jp") {
      return "font-sans-ja";
    }
    return "font-sans-en";
  };

  return auth.loading && isPublisherLoading && isAuthMeLoading ? (
    <div>Loading...</div>
  ) : (
    <div className={getFontClass()}>
      <Router>
        <Routes>
          {auth?.isAuth ? (
            <>
              <Route
                path="/"
                element={
                  <DashboardLayout
                    signout={auth.signout}
                    lang={lang}
                    country={country}
                    setCountry={setCountry}
                    setLang={setLang}
                    credits={getCredit(
                      authMeResponse?.credits,
                      country
                    ).toString()}
                    isPublisherInfoLoading={isPublisherLoading}
                    is_subscripted={checkSubscripted(
                      authMeResponse?.credits,
                      country
                    )}
                    setPath={setPath}
                    defaultCountry={defaultCountry}
                  />
                }
              >
                <Route
                  path="article/:publisherId/:slug/:step"
                  element={
                    <EditArticlePage
                      auth={auth}
                      country={country}
                      credits={getCredit(authMeResponse?.credits, country)}
                      creditInfo={authMeResponse?.credits}
                      refetchPublisher={refetchAll}
                      publisherResponse={publisherResponse}
                      remaining_quota={
                        paymentQuotaResponse?.credits?.find(
                          (c) => c.country === country
                        )?.subscription.remainingQuota
                      }
                      defaultCountry={publisherResponse?.country}
                    />
                  }
                />
                <Route
                  path="settings"
                  element={
                    <EditPublisherInfoPage
                      auth={auth}
                      country={country}
                      refetchPublisher={refetchAll}
                    />
                  }
                />
                <Route
                  path="credits_management"
                  element={
                    defaultCountry === "JPN" ? (
                      <Navigate to="/dashboard" replace />
                    ) : (
                      <CreditsmanagementPage
                        auth={auth}
                        country={country}
                        defaultCountry={publisherResponse?.country}
                        credits={getCredit(
                          authMeResponse?.credits,
                          country
                        ).toString()}
                        isPublisherInfoLoading={isPublisherLoading}
                        is_subscripted={checkSubscripted(
                          authMeResponse?.credits,
                          country
                        )}
                        remaining_quota={
                          paymentQuotaResponse?.credits?.find(
                            (c) => c.country === country
                          )?.subscription.remainingQuota
                        }
                        quota_reset_day={
                          paymentQuotaResponse?.credits?.find(
                            (c) => c.country === country
                          )?.subscription.quotaResetDay
                        }
                      />
                    )
                  }
                />
                <Route
                  path="report"
                  element={
                    <ReportPage
                      signout={auth.signout}
                      lang={lang}
                      country={country}
                      publisherId={publisherResponse?.id}
                      isPublisherLoading={isPublisherLoading}
                    />
                  }
                />
                <Route
                  path="article/new"
                  element={<SelectGenerationMethodPage country={country} />}
                />
                <Route
                  path="article/new/manual"
                  element={
                    <CreateArticlePage
                      auth={auth}
                      country={country}
                      publisherResponse={publisherResponse}
                      credits={getCredit(authMeResponse?.credits, country)}
                      creditInfo={authMeResponse?.credits}
                      refetchPublisher={refetchAll}
                      remaining_quota={
                        paymentQuotaResponse?.credits?.find(
                          (c) => c.country === country
                        )?.subscription.remainingQuota
                      }
                      defaultCountry={publisherResponse?.country}
                    />
                  }
                />
                <Route
                  path="article/new/ai"
                  element={<AiChatPage country={country} />}
                />
                <Route
                  path="additional_service"
                  element={<AdditionalServicePage country={country} />}
                />
              </Route>
              <Route
                path="/"
                element={
                  <DashboardLayout
                    signout={auth.signout}
                    lang={lang}
                    country={country}
                    setCountry={setCountry}
                    setLang={setLang}
                    credits={getCredit(
                      authMeResponse?.credits,
                      country
                    ).toString()}
                    isPublisherInfoLoading={isPublisherLoading}
                    is_subscripted={checkSubscripted(
                      authMeResponse?.credits,
                      country
                    )}
                    defaultCountry={defaultCountry}
                  />
                }
              >
                <Route
                  path="dashboard"
                  element={
                    <DashboardPage
                      signout={auth.signout}
                      lang={lang}
                      country={country}
                      setCountry={setCountry}
                      publisherId={publisherResponse?.id}
                      publisherName={publisherResponse?.name}
                      isPublisherLoading={isPublisherLoading}
                    />
                  }
                />
              </Route>
              <Route path="*" element={<Navigate to={path} />} />
            </>
          ) : (
            <Route path="/" element={<SignInLayout />}>
              <Route path="/" element={<Navigate to="signin" />} />
              <Route
                path="signin"
                element={
                  <SignInPage
                    signIn={auth?.signin}
                    signOut={auth?.signout}
                    lang={lang}
                    setPath={setPath}
                    path={path}
                  />
                }
              />
              <Route path="/auth/signup" element={<SignUpPage lang={lang} />} />
              <Route
                path="/auth/reset-password"
                element={<ResetPasswordPage lang={lang} />}
              />
              <Route path="*" element={<Navigate to="signin" />} />
            </Route>
          )}
        </Routes>
      </Router>
    </div>
  );
};

/**
 * Return path
 */
const getPath = () => {
  const url = decodeURIComponent(window.location.href);
  const urlParams = new URLSearchParams(url.split("?")[1]);
  const result = urlParams.get("result");
  const country = urlParams.get("country");

  const urlObject = new URL(url);
  const { pathname } = urlObject;

  if (pathname === "/signin") {
    return "dashboard";
  }
  if (pathname === "/dashboard") {
    if (country) return `${pathname}?country=${country}`;
    return pathname;
  }
  if (
    pathname === "/article/new" ||
    pathname === "/article/new/manual" ||
    pathname === "/article/new/ai"
  ) {
    if (country) return `${pathname}?country=${country}`;
    return pathname;
  }
  if (pathname === "/article/:publisherId/:slug") {
    if (country) return `${pathname}?country=${country}`;
    return pathname;
  }
  if (pathname === "/settings") {
    if (country) return `${pathname}?country=${country}`;
    return pathname;
  }
  if (pathname === "/credits_management") {
    if (result && country)
      return `${pathname}?result=${result}&country=${country}`;
    if (result) return `${pathname}?result=${result}`;
    if (country) return `${pathname}?country=${country}`;
    return pathname;
  }
  if (pathname === "/report") {
    if (country) return `${pathname}?country=${country}`;
    return pathname;
  }
  if (pathname === "/additional_service") {
    if (country) return `${pathname}?country=${country}`;
    return pathname;
  }
  return "dashboard";
};

const getCountry = (country: string | CountryPath) => {
  if (
    country === "IDN" ||
    country === "SGP" ||
    country === "MYS" ||
    country === "PHL" ||
    country === "THA" ||
    country === "VNM" ||
    country === "AUS"
  ) {
    return country;
  }
  return "";
};

export default Root;
